import { z } from 'zod';
const nonProperty = /(\s(?:&|and|at)\s|\bintersection\b|\bp\.?o\.?\s*box\b)/i;
export const PropertyAddressSchema = z.object({
  street: z
    .string()
    .trim()
    .min(3)
    .max(160)
    .regex(/^\d+[A-Za-z]?(?:[-/]\d+)?\s+\S+/, 'Enter a street number and street name')
    .refine((s) => !nonProperty.test(s), 'Enter a property address, not an intersection or PO box'),
  unit: z.string().trim().max(40).default(''),
  city: z.string().trim().min(1).max(100),
  region: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Use the two-letter state code'),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}(?:-\d{4})?$/, 'Enter a ZIP code'),
  country: z.literal('US').default('US'),
  placeId: z.string().max(300).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});
export type PropertyAddressInput = z.infer<typeof PropertyAddressSchema>;
export function addressLabel(a: PropertyAddressInput) {
  return `${a.street}${a.unit ? ` #${a.unit}` : ''}, ${a.city}, ${a.region} ${a.postalCode}`;
}
export function normalizeAddress<T extends { street: string; unit: string; city: string }>(a: T) {
  const suffixes: Record<string, string> = {
    street: 'St',
    avenue: 'Ave',
    road: 'Rd',
    boulevard: 'Blvd',
    drive: 'Dr',
    lane: 'Ln',
    court: 'Ct',
    place: 'Pl',
    terrace: 'Ter',
    circle: 'Cir',
    highway: 'Hwy',
  };
  return {
    ...a,
    street: a.street
      .replace(/\s+/g, ' ')
      .replace(
        /\b(street|avenue|road|boulevard|drive|lane|court|place|terrace|circle|highway)\.?$/i,
        (s) => suffixes[s.toLowerCase().replace('.', '')]!,
      ),
    unit: a.unit.replace(/^(?:apt\.?|unit|suite|ste\.?|#)\s*/i, '').trim(),
    city: /^san\s+francisco$/i.test(a.city.trim()) ? 'San Francisco' : a.city.replace(/\s+/g, ' '),
  };
}
